import AirDatepicker, {
  type AirDatepickerLocale,
  type AirDatepickerOptions,
  type AirDatepickerPosition,
} from 'air-datepicker';
import localeEn from 'air-datepicker/locale/en';
import localeId from 'air-datepicker/locale/id';
import localeKo from 'air-datepicker/locale/ko';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import WifiIcon from '@/assets/smartcity/ambulance-icon.svg';
import Ambulance from '@/assets/smartcity/ambulance-legend.svg';
// legendMonitoring
import CCTV from '@/assets/smartcity/cctv-legend.svg';
import FireStation from '@/assets/smartcity/fireStation-legend.svg';
import Government from '@/assets/smartcity/government-legend.svg';
import Healthcare from '@/assets/smartcity/healthcare-legend.svg';
import Landmark from '@/assets/smartcity/landmark-legend.svg';
import Morning from '@/assets/smartcity/morning-icon.svg';
import Police from '@/assets/smartcity/poice-legend.svg';
import TrafficLight from '@/assets/smartcity/traffic-legend.svg';
import Wifi from '@/assets/smartcity/wifi-legend.svg';
import CardControl from '@/domains/traffic-monitoring/components/CardControl/CardControl';
import CardEventSmartCity from '@/domains/traffic-monitoring/components/CardEventSmartCity/CardEventSmartCity';
import SmartCityMaps, {
  type CctvClickData,
} from '@/domains/traffic-monitoring/components/SmartCityMaps/SmartCityMaps';
import TimeFilter from '@/domains/traffic-monitoring/components/TimeFilter/TimeFilter';
import VideoModalMaps from '@/domains/traffic-monitoring/components/VideoModalMaps/VideoModalMaps';
import { useSmartCityDashboard } from '@/domains/traffic-monitoring/hooks/useSmartCityDashboard';
import {
  getWeatherConditionKey,
  getWeatherIcon,
} from '@/domains/traffic-monitoring/utils/weatherIcon';
import { cn } from '@/lib/utils';
import Header from '@/shared/components/layout/Header/Header';
// import DatePicker from '@/shared/components/ui/DatePicker/DatePicker';
import GeneralInput from '@/shared/components/ui/GeneralInput/GeneralInput';
import LineChart, { type SeriesItem } from '@/shared/components/ui/LineChart/LineChart';
import { formatDateTime, formatDateToYYYYMMDD } from '@/shared/utils/date';
import { ButtonStore } from '@/store/buttonStore';

const options = ['1h', '6h', '12h', '24h'] as const;

// maps
const coords = {
  lat: -7.13,
  lng: 111.02,
};

const SmartCityDashboard = () => {
  const { t, i18n } = useTranslation();
  const setActiveIds = ButtonStore((state) => state.setActiveIds);
  const activeTraffic = ButtonStore((state) => state.activeIdsMap['traffic'] || []);
  const [date, setDate] = useState('');
  const [isOpen, setIsOpen] = useState(true);
  const [isLeftOpen, setisLeftOpen] = useState(true);
  const [filterVolume, setFilterVolume] = useState('1h');
  const [filterAbnormal, setFilterAbnormal] = useState('1h');
  const [expandedCctv, setExpandedCctv] = useState<CctvClickData | null>(null);

  const today = new Date();

  const [dateTime, setDateTime] = useState({
    start_date: formatDateToYYYYMMDD(today),
  });

  // Query backend data via useSmartCityDashboard hook
  const { poiData, events, weather, anomalyTrend, volumeTrend, isPending, isError } =
    useSmartCityDashboard({
      siteId: 1,
      date: dateTime.start_date ? dateTime.start_date.split(' ')[0] : undefined,
      filterAbnormal,
      filterVolume,
    });

  useEffect(() => {
    setActiveIds('traffic', [
      'cctvpoi',
      'policepoi',
      'healthcarepoi',
      'ambulancepoi',
      'governmentpoi',
      'fireStation',
      'wifipoi',
      'trafficlightpoi',
      'sensor',
      'incident',
      'startpoint',
    ]);
  }, [setActiveIds]);

  // Dynamic Markers & POI setup (poiData is already flattened by the hook)
  const apiMarkers = poiData.map((poi) => ({
    id: String(poi.id),
    lat: poi.lat,
    lng: poi.lng,
    type: poi.type,
    tooltip: poi.tooltip || {
      title: poi.name || poi.type,
      description: poi.description || '',
    },
    cctv: poi.cctv,
  }));

  const displayMarkers = apiMarkers;
  // Compare case-insensitively on both sides: marker `type` keys are lowercase
  // (e.g. 'firestation') while the active-toggle ids keep their original casing
  // (e.g. 'fireStation'), so a raw includes() would drop those layers.
  const activeTrafficLower = activeTraffic.map((id) => id.toLowerCase());
  const filteredMarkers = displayMarkers.filter((marker) =>
    activeTrafficLower.includes(marker.type.toLowerCase())
  );

  const getMarkerCount = (type: string) => {
    return displayMarkers.filter((m) => m.type.toLowerCase() === type.toLowerCase()).length;
  };

  // Report event-list item click → fly the map to that incident's marker.
  // nonce bumps each click so clicking the same item twice still re-triggers
  // the animation (matches PSCDashboard.tsx's own focusTarget pattern).
  const [focusTarget, setFocusTarget] = useState<{
    lat: number;
    lng: number;
    nonce: number;
    id: string;
  } | null>(null);
  const markersById = new Map(displayMarkers.map((marker) => [marker.id, marker]));
  const focusOnMarker = (markerId: string) => {
    const marker = markersById.get(markerId);
    if (!marker) return;
    setFocusTarget((prev) => ({
      lat: marker.lat,
      lng: marker.lng,
      nonce: (prev?.nonce ?? 0) + 1,
      id: markerId,
    }));
  };

  // Dynamic Events list
  const displayEvents = events;

  // Dynamic Weather
  const displayWeatherForecast = weather?.forecast ?? [];

  // Dynamic Chart Series
  const dynamicXAxisAbnormal = anomalyTrend?.times ?? [];

  const dynamicSeriesAbnormal: SeriesItem[] = anomalyTrend
    ? [
        {
          name: 'PSC',
          color: 'warning',
          data: anomalyTrend.report_data || [],
          areaStyle: {
            startColor: 'warning',
            endColor: 'greyPrimary',
            opacity: 0.3,
          },
        },
        {
          name: 'Traffic',
          color: 'danger',
          data: anomalyTrend.total_data || [],
          areaStyle: {
            startColor: 'accentSoftRed',
            endColor: 'greyPrimary',
            opacity: 0.3,
          },
        },
      ]
    : [];

  const dynamicXAxisVolume = volumeTrend?.times ?? [];

  // Grouped by vehicle type (see VEHICLE_VOLUME_BUCKETS), one line per bucket -
  // same grouping the PC0203001 by-vehicle peak-hours chart uses.

  const dynamicSeriesVolume =
    volumeTrend?.series?.map((series) => ({
      ...series,
      name: t(`traffic>vehicle>${series.name}`),
    })) ?? [];

  useEffect(() => {
    let locale: AirDatepickerLocale;
    if (i18n.language === 'eng') {
      locale = localeEn;
    } else if (i18n.language === 'ind') {
      locale = localeId;
    } else {
      locale = localeKo;
    }
    const today = new Date();
    const optionsDate: AirDatepickerOptions = {
      autoClose: true,
      locale: locale,
      position: 'top center' as unknown as AirDatepickerPosition,
      selectedDates: [today],
      timepicker: false,
      timeFormat: 'HH:mm',
      dateFormat: 'yyyy-MM-dd',
      onSelect: (date) => {
        setDateTime((prevValues) => ({
          ...prevValues,
          start_date: date.formattedDate as string,
        }));
      },
    };

    const datepicker1 = new AirDatepicker('[name="start_date"]', optionsDate);

    return () => {
      datepicker1.destroy();
    };
  }, [i18n.language]);

  return (
    <section className="_mainDashboard bg-db-wrap flex flex-col gap-[12px] w-full h-screen overflow-hidden">
      <Header />
      <div
        className={`
        grid w-full h-full overflow-hidden  duration-500 ease-in-out ${isLeftOpen ? 'grid-cols-[420px_1fr] ' : 'grid-cols-[0%_1fr]'}
        `}
      >
        {/* section left */}
        <section className="w-full flex! gap-6 p-6 overflow-hidden  flex-col h-full border-r bg-db-card bg-db-border ">
          <div className="w-full flex-[1.25] overflow-hidden flex flex-col gap-3   ">
            <span className="title1bold text-db-text capitalize">{t('cmn>master>Monitoring')}</span>
            <div className="w-full flex-1 overflow-auto flex flex-col gap-3  ">
              <CardControl
                icon={Ambulance}
                title={t('cmn>monitoring>Active Ambulance')}
                total={getMarkerCount('ambulancepoi')}
                groupKey="traffic"
                id="ambulancepoi"
              />
              <CardControl
                icon={CCTV}
                title={t('cmn>monitoring>CCTV')}
                total={getMarkerCount('cctvpoi')}
                groupKey="traffic"
                id="cctvpoi"
              />
              <CardControl
                icon={Police}
                title={t('cmn>monitoring>police station')}
                total={getMarkerCount('policepoi')}
                groupKey="traffic"
                id="policepoi"
              />
              <CardControl
                icon={Healthcare}
                title={t('cmn>monitoring>healthcare')}
                total={getMarkerCount('healthcarepoi')}
                groupKey="traffic"
                id="healthcarepoi"
              />

              <CardControl
                icon={Government}
                title={t('cmn>monitoring>Government')}
                total={getMarkerCount('governmentpoi')}
                groupKey="traffic"
                id="governmentpoi"
              />
              <CardControl
                icon={FireStation}
                title={t('cmn>monitoring>Fire Station')}
                total={getMarkerCount('fireStation')}
                groupKey="traffic"
                id="fireStation"
              />
              <CardControl
                icon={Wifi}
                title={t('cmn>monitoring>Wifi Location')}
                total={getMarkerCount('wifipoi')}
                groupKey="traffic"
                id="wifipoi"
              />
              <CardControl
                icon={TrafficLight}
                title={t('cmn>monitoring>traffic lights')}
                total={getMarkerCount('trafficlightpoi')}
                groupKey="traffic"
                id="trafficlightpoi"
              />
            </div>
          </div>
          <div className="w-full flex-1 overflow-hidden flex flex-col gap-3   ">
            <div className="grid grid-cols-[repeat(auto-fit,minmax(130px,1fr))]  gap-3 justify-between items-center">
              <span className="title1bold  text-db-text capitalize w-fit">
                {t('cmn>master>event list')}{' '}
                <span className="title2 text-text-grey">
                  {displayEvents.length} {t('cmn > total')}
                </span>{' '}
              </span>
              <GeneralInput
                isDob={true}
                type="text"
                name={'start_date'}
                value={dateTime.start_date}
                customInput="min-w-0 flex-1"
                inputFormStyle="w-full bg-[#F3F6FE]! dark:bg-[#F3F6FE]/10! border-[#C2D2E2]! dark:border-[#37424D]! rounded-[12px]! h-[40px]! title2!"
              />
            </div>
            <div className="w-full flex-1 overflow-auto flex flex-col gap-3 pb-[50px]">
              <div className="w-full flex-1 overflow-auto flex flex-col gap-3 pb-[50px]">
                {displayEvents.map((event) => {
                  const handleClick = () => {
                    if (event.type === 'report' && event.reportId) {
                      focusOnMarker(`incident-${event.reportId}`);
                      return;
                    }

                    if (event.type === 'event' && event.eventId) {
                      const params = new URLSearchParams({ type: 'event', id: event.eventId });
                      window.open(
                        `/dashboard/event-dashboard?${params.toString()}`,
                        '_blank',
                        'width=760,height=500,resizable=yes,scrollbars=yes'
                      );
                      return;
                    }
                  };

                  return (
                    <CardEventSmartCity
                      key={event.id}
                      title={event.title}
                      address={event.address || ''}
                      dateTime={event.dateTime}
                      eventListRoadType={event.eventListRoadType}
                      onclick={handleClick}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        </section>
        <section className="w-full h-full relative  bg-db-card ">
          <button
            onClick={() => setisLeftOpen((prev) => !prev)}
            type="button"
            className="bg-text-blue-100 rounded-r-[20px] w-[30px] border h-[100px] absolute left-0 top-[50%] -translate-y-1/2 z-[10] flex items-center justify-center"
          >
            {isLeftOpen ? (
              <ChevronLeft className="text-white" />
            ) : (
              <ChevronRight className="text-white" />
            )}
          </button>
          <SmartCityMaps
            coords={coords}
            zoom={12}
            markers={filteredMarkers}
            focusTarget={focusTarget}
            onCctvExpand={setExpandedCctv}
            // Keep the map readable: camera pins show their video card only
            // after the pin itself is clicked; clicking the map closes them.
            openCctvCardsOnClick
            // Tooltips show name/description details, not lat/lng.
            hideCoordinates
          />
        </section>

        {/* section right */}
        <section
          className={`
          sideright
          absolute
          w-[500PX]
          right-[12px]
          top-[70px]
          flex
          gap-3
          flex-col
          h-[calc(100vh-80px)]
          transform
          transition-transform
          duration-500
          ease-in-out
          ${isOpen ? 'translate-x-0' : 'translate-x-[calc(100%-15px)]'}
        `}
        >
          <div className="w-full h-full flex flex-col pl-6  gap-3  relative ">
            <button
              onClick={() => setIsOpen((prev) => !prev)}
              type="button"
              className="bg-text-blue-100 rounded-l-[20px] w-[30px] border h-[100px] absolute left-0 top-[50%]"
            >
              {isOpen ? (
                <ChevronRight className="text-white" />
              ) : (
                <ChevronLeft className="text-white" />
              )}
            </button>

            <div className="w-full h-full flex flex-col p-3  gap-3 overflow-auto relative bg-white/20 dark:bg-white/5 backdrop-blur-xl border shadow-xl rounded-[12px]">
              <div className="border p-6 flex flex-col gap-3 w-full h-fit bg-db-card bg-db-border rounded-[12px]">
                <div className=" flex  w-full flex-row gap-3 items-center justify-between">
                  <span className="title1bold capitalize">{t('cmn>master>local weather')}</span>
                  <span className="title2 capitalize text-text-grey">
                    {t('cmn>master>updated')} {weather?.updated_at || t('cmn>master>2 minutes ago')}
                  </span>
                </div>
                <div className="flex flex-row items-center gap-3 justify-between">
                  <div className="items-center flex flex-row gap-1">
                    <img src={getWeatherIcon(weather?.icon, weather?.condition)} alt="weather" />
                    <div className="flex flex-col">
                      <span className="title1bold text-text-blue-100">
                        {weather?.temp_c !== undefined ? `${weather.temp_c}°C` : '28°C'}
                      </span>
                      <span className="title2">
                        {t(getWeatherConditionKey(weather?.icon, weather?.condition))}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col">
                    <span className="title2 text-text-grey">{t('cmn > humidity')}</span>
                    <span className="title1bold text-text-blue-100">
                      {weather?.humidity_pct !== undefined ? `${weather.humidity_pct}%` : '84%'}
                    </span>
                  </div>
                </div>
                {/* <div className="w-full grid grid-cols-4 gap-4 items-center">
              {displayWeatherForecast.map((wForecast) => (
                <div
                  key={wForecast.id}
                  className="w-full rounded-[12px] items-center py-3 px-6 flex flex-col gap-1 border bg-db-border bg-white dark:bg-[#F3F6FE]/10"
                >
                  <span className="title2 text-text-grey ">{wForecast.time}</span>
                  <img src={wForecast.img || Morning} alt="icon" />
                  <span className="title2bold text-text-grey ">{wForecast.celcius}</span>
                </div>
              ))}
            </div> */}
              </div>

              <div className="border p-6 flex flex-col w-full min-h-[270px] flex-1 bg-db-card bg-db-border rounded-[12px]">
                <div className="w-full items-center flex gap-3 justify-between">
                  <span className="title1bold capitalize">{t('cmn>master>volume trends')}</span>
                  <TimeFilter options={options} value={filterVolume} onChange={setFilterVolume} />
                </div>
                <div className="w-full h-full">
                  <LineChart
                    xAxisData={dynamicXAxisVolume}
                    series={dynamicSeriesVolume}
                    boundaryGap={false}
                    bottom={'70px'}
                  />
                </div>
              </div>

              <div className="border p-6 flex flex-col w-full min-h-[270px] flex-1 bg-db-card bg-db-border rounded-[12px]">
                <div className="w-full items-center flex gap-3 justify-between">
                  <span className="title1bold capitalize">
                    {t('cmn>master>abnormality trends')}
                  </span>
                  <TimeFilter
                    options={options}
                    value={filterAbnormal}
                    onChange={setFilterAbnormal}
                  />
                </div>
                <div className="w-full h-full">
                  <LineChart
                    xAxisData={dynamicXAxisAbnormal}
                    series={dynamicSeriesAbnormal}
                    boundaryGap={false}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <VideoModalMaps
        isOpen={!!expandedCctv}
        address={expandedCctv?.address ?? ''}
        name={expandedCctv?.name ?? ''}
        latlong={expandedCctv?.latlong ?? ''}
        streamUrl={expandedCctv?.streamUrl ?? undefined}
        typeVideo={expandedCctv?.typeVideo ?? 'offline'}
        onClick={() => setExpandedCctv(null)}
      />
    </section>
  );
};

export default SmartCityDashboard;
